import type { ProfileField } from "../types/profile";

/**
 * Each rule uses getEffectiveValue — must match BusinessInformationSection
 * contact-information display bindings (e.g. ci.state ?? ci.companyState).
 * Using .some() on duplicate API keys incorrectly passed validation when the
 * user cleared the editor field but the alias still held the old value.
 */
export const CONTACT_VALIDATION_SEQUENCE = [
  {
    focusId: "profile-contact-country-anchor",
    displayKey: "companyCountry",
    sourceKeys: ["companyCountry", "country"],
    message: "Please select your country",
    getEffectiveValue: (ci: Record<string, ProfileField> | undefined | null) =>
      (ci?.companyCountry ?? ci?.country)?.value,
  },
  {
    focusId: "profile-contact-address",
    displayKey: "address",
    sourceKeys: ["address", "companyAddress"],
    message: "Please enter your business address",
    getEffectiveValue: (ci: Record<string, ProfileField> | undefined | null) =>
      (ci?.address ?? ci?.companyAddress)?.value,
  },
  {
    focusId: "profile-contact-state",
    displayKey: "state",
    sourceKeys: ["state", "companyState"],
    message: "Please enter your state or province",
    getEffectiveValue: (ci: Record<string, ProfileField> | undefined | null) =>
      (ci?.state ?? ci?.companyState)?.value,
  },
  {
    focusId: "profile-contact-city",
    displayKey: "city",
    sourceKeys: ["city", "companyCity"],
    message: "Please enter your city",
    getEffectiveValue: (ci: Record<string, ProfileField> | undefined | null) =>
      (ci?.city ?? ci?.companyCity)?.value,
  },
  {
    focusId: "profile-contact-postalCode",
    displayKey: "postalCode",
    sourceKeys: ["postalCode", "companyPostalCode"],
    message: "Please enter your pincode",
    getEffectiveValue: (ci: Record<string, ProfileField> | undefined | null) =>
      (ci?.postalCode ?? ci?.companyPostalCode)?.value,
  },
  {
    focusId: "profile-contact-companyPhone",
    displayKey: "companyPhone",
    sourceKeys: ["companyPhone", "phone"],
    message: "Please enter your mobile number",
    /** Input only binds to companyPhone — ignore stale `phone` for emptiness. */
    getEffectiveValue: (ci: Record<string, ProfileField> | undefined | null) =>
      ci?.companyPhone?.value,
  },
  {
    focusId: "profile-contact-companyEmail",
    displayKey: "companyEmail",
    sourceKeys: ["companyEmail"],
    message: "Please add your business email in account settings—this field is required.",
    getEffectiveValue: (ci: Record<string, ProfileField> | undefined | null) =>
      ci?.companyEmail?.value,
  },
] as const;

function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

export function getFirstContactInformationViolation(
  companyInfo: Record<string, ProfileField> | undefined | null,
): { displayKey: string; message: string; focusId: string } | null {
  const ci = companyInfo;
  for (const rule of CONTACT_VALIDATION_SEQUENCE) {
    const effective = rule.getEffectiveValue(ci);
    if (isEmpty(effective)) {
      return {
        displayKey: rule.displayKey,
        message: rule.message,
        focusId: rule.focusId,
      };
    }
    if (rule.displayKey === "companyPhone") {
      const digits = String(effective).replace(/\D/g, "");
      if (digits.length !== 10) {
        return {
          displayKey: rule.displayKey,
          message: "Mobile number must be exactly 10 digits",
          focusId: rule.focusId,
        };
      }
    }
  }
  return null;
}

export function findContactRuleForFieldKey(fieldKey: string) {
  return CONTACT_VALIDATION_SEQUENCE.find(
    (r) =>
      r.displayKey === fieldKey ||
      (r.sourceKeys as readonly string[]).includes(fieldKey),
  );
}

/** Focus the first incomplete contact field control (after DOM updates). */
export function focusContactInformationField(focusId: string): void {
  window.requestAnimationFrame(() => {
    let el: HTMLElement | null = null;
    if (focusId === "profile-contact-country-anchor") {
      el = document.querySelector(
        "#profile-contact-country-anchor button[role=combobox]",
      ) as HTMLElement | null;
    } else {
      el = document.getElementById(focusId);
    }
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    el?.focus({ preventScroll: true });
  });
}
